import React, { useState } from 'react';
import * as S from './AdvancesStyles';

const CATS = ['Sugar', 'Medicine', 'Labour', 'Transport', 'Maintenance', 'Other'];
const CAT_COLOR = {
  Sugar: '#EA5B2A',
  Medicine: '#2F6FED',
  Labour: '#1E9E5A',
  Transport: '#8B5CE0',
  Maintenance: '#B9791A',
  Other: '#6B7280'
};

const fmt = n => (n < 0 ? '−₹' : '₹') + Math.round(Math.abs(n)).toLocaleString('en-IN');
const fmtDate = d => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};
const sum = (arr, f) => arr.reduce((s, x) => s + f(x), 0);

/* --- Interactive SVG Donut Chart with Floating Tooltip --- */
const DonutChartSVG = ({ data, labels, colors, counts = [] }) => {
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const total = data.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
        No spend data recorded yet for this apiary.
      </div>
    );
  }

  let cumulativeAngle = 0;
  const radius = 64;
  const strokeWidth = 20;
  const center = 85;
  const circumference = 2 * Math.PI * radius;

  const slices = data.map((val, idx) => {
    const percentage = val / total;
    const strokeDasharray = `${percentage * circumference} ${circumference}`;
    const strokeDashoffset = -cumulativeAngle * circumference;
    cumulativeAngle += percentage;
    return {
      label: labels[idx],
      val,
      pct: percentage,
      count: counts[idx] || 0,
      color: colors[labels[idx]] || '#6B7280',
      strokeDasharray,
      strokeDashoffset
    };
  });

  const activeSlices = slices.filter(s => s.val > 0);

  const handleMouseMove = (e, slice) => {
    const rect = e.currentTarget.closest('.donut-container').getBoundingClientRect();
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setHoveredSlice(slice);
  };

  return (
    <div className="donut-container" style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px 0' }}>
      {/* Floating Tooltip */}
      {hoveredSlice && (
        <div
          style={{
            position: 'absolute',
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            transform: 'translate(-50%, -120%)',
            background: 'rgba(24, 26, 32, 0.94)',
            color: '#FFFFFF',
            padding: '7px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            pointerEvents: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            zIndex: 100,
            whiteSpace: 'nowrap',
            transition: 'top 0.05s ease, left 0.05s ease'
          }}
        >
          <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '2px' }}>{hoveredSlice.label}</div>
          <div style={{ fontSize: '13px', fontWeight: 800 }}>
            {fmt(hoveredSlice.val)}{' '}
            <span style={{ fontSize: '11px', color: '#6EE7B7', fontWeight: 600 }}>
              ({(hoveredSlice.pct * 100).toFixed(1)}%)
            </span>
          </div>
          <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
            {hoveredSlice.count} transaction{hoveredSlice.count === 1 ? '' : 's'}
            {hoveredSlice.count > 0 && ` · avg ${fmt(hoveredSlice.val / hoveredSlice.count)}`}
          </div>
        </div>
      )}

      <svg width="170" height="170" viewBox="0 0 170 170" style={{ overflow: 'visible' }}>
        {slices.map((slice, idx) => {
          if (slice.val <= 0) return null;
          const isHovered = hoveredSlice?.label === slice.label;
          return (
            <circle
              key={idx}
              cx={center}
              cy={center}
              r={radius}
              fill="transparent"
              stroke={slice.color}
              strokeWidth={isHovered ? strokeWidth + 4 : strokeWidth}
              strokeDasharray={slice.strokeDasharray}
              strokeDashoffset={slice.strokeDashoffset}
              transform={`rotate(-90 ${center} ${center})`}
              style={{
                cursor: 'pointer',
                transition: 'stroke-width 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
                opacity: hoveredSlice && !isHovered ? 0.6 : 1
              }}
              onMouseMove={e => handleMouseMove(e, slice)}
              onMouseLeave={() => setHoveredSlice(null)}
            />
          );
        })}
        <text
          x={center}
          y={center - 3}
          textAnchor="middle"
          fontSize="14"
          fontWeight="800"
          fill="#181A20"
        >
          {fmt(total)}
        </text>
        <text
          x={center}
          y={center + 14}
          textAnchor="middle"
          fontSize="10"
          fontWeight="600"
          fill="#9CA3AF"
        >
          Total Spend
        </text>
      </svg>

      {/* Clean Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', marginTop: '16px', justifyContent: 'center' }}>
        {activeSlices.map((slice, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              padding: '3px 8px',
              borderRadius: '6px',
              background: '#F9FAFB',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredSlice(slice)}
            onMouseLeave={() => setHoveredSlice(null)}
          >
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: slice.color }} />
            <span style={{ fontWeight: 600, color: '#374151' }}>{slice.label}:</span>
            <span style={{ fontWeight: 700, color: '#111827' }}>{fmt(slice.val)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* --- Interactive SVG Vertical Bar Chart with Hover Tooltip --- */
const VerticalBarChartSVG = ({ labels, data, counts = [], yearLabels = [] }) => {
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const maxVal = Math.max(...data, 1000);
  const width = 340;
  const height = 170;

  const handleMouseMove = (e, item) => {
    const rect = e.currentTarget.closest('.bar-container').getBoundingClientRect();
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setHoveredBar(item);
  };

  return (
    <div className="bar-container" style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px 0' }}>
      {/* Floating Tooltip */}
      {hoveredBar && (
        <div
          style={{
            position: 'absolute',
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            transform: 'translate(-50%, -120%)',
            background: 'rgba(24, 26, 32, 0.94)',
            color: '#FFFFFF',
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            pointerEvents: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            zIndex: 100,
            whiteSpace: 'nowrap'
          }}
        >
          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{hoveredBar.label} {hoveredBar.year || ''}</div>
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#FF7A45' }}>{fmt(hoveredBar.val)}</div>
          <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
            {hoveredBar.count} transaction{hoveredBar.count === 1 ? '' : 's'}
          </div>
        </div>
      )}

      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
        {/* Subtle Horizontal grid lines */}
        {[0.25, 0.5, 0.75, 1].map((ratio, i) => {
          const y = height - 28 - (height - 55) * ratio;
          return (
            <line
              key={i}
              x1="20"
              y1={y}
              x2={width - 20}
              y2={y}
              stroke="#F3F4F6"
              strokeDasharray="3 3"
              strokeWidth="1"
            />
          );
        })}

        {data.map((val, idx) => {
          const item = { label: labels[idx], val, count: counts[idx] || 0, year: yearLabels[idx] };
          const barHeight = val > 0 ? Math.max((val / maxVal) * (height - 60), 6) : 3;
          const barWidth = 44;
          const x = 32 + idx * ((width - 64) / data.length) + (((width - 64) / data.length - barWidth) / 2);
          const y = height - 28 - barHeight;
          const isHovered = hoveredBar?.label === labels[idx];

          return (
            <g
              key={idx}
              style={{ cursor: 'pointer' }}
              onMouseMove={e => handleMouseMove(e, item)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              {/* Invisible Hover Target */}
              <rect
                x={x - 6}
                y={20}
                width={barWidth + 12}
                height={height - 40}
                fill="transparent"
              />
              {/* The Visual Bar */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={isHovered ? '#D04A1B' : '#EA5B2A'}
                rx="6"
                style={{
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  filter: isHovered ? 'drop-shadow(0 4px 10px rgba(234, 91, 42, 0.4))' : 'none'
                }}
              />
              {/* Value on Top */}
              <text
                x={x + barWidth / 2}
                y={y - 8}
                textAnchor="middle"
                fontSize="11"
                fontWeight="700"
                fill={val > 0 ? '#181A20' : '#9CA3AF'}
              >
                {fmt(val)}
              </text>
              {/* Label at Bottom */}
              <text
                x={x + barWidth / 2}
                y={height - 10}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill={isHovered ? '#181A20' : '#6B7280'}
              >
                {labels[idx]}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const ApiaryPnLTab = ({
  txns,
  apiaries,
  projects,
  member,
  project,
  onOpenPassbook
}) => {
  const [selectedApiaryId, setSelectedApiaryId] = useState(
    apiaries.length ? apiaries[0].id : ''
  );

  const apiaryRows = txns.filter(
    t => t.type === 'expense' && t.tagType === 'apiary' && t.locId === selectedApiaryId
  );
  const approvedRows = apiaryRows.filter(t => t.approvalStatus === 'approved');
  const totalCost = sum(approvedRows, t => t.amount);
  const pendingCost = sum(
    apiaryRows.filter(t => t.approvalStatus === 'pending'),
    t => t.amount
  );
  const approvedCount = approvedRows.length;
  const avgCost = approvedCount ? totalCost / approvedCount : (apiaryRows.length ? (totalCost + pendingCost) / apiaryRows.length : 0);

  // Active rows for charts (use approved if available, else all logged for this apiary)
  const activeChartRows = approvedRows.length ? approvedRows : apiaryRows;

  const catRows = CATS.map(c => activeChartRows.filter(t => t.category === c));
  const catTotals = catRows.map(rows => sum(rows, t => t.amount));
  const catCounts = catRows.map(rows => rows.length);

  // Rolling 4-month window ending this month, computed from the real current date —
  // see OverviewTab.jsx for why this replaced a hardcoded May-Aug range.
  const months = Array.from({ length: 4 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - (3 - i));
    return d.toISOString().slice(0, 7);
  });
  const monthLabels = months.map(m => new Date(m + '-02').toLocaleString('default', { month: 'short' }));
  const monthYearLabels = months.map(m => m.slice(0, 4));
  const monthRows = months.map(mo => activeChartRows.filter(t => t.date && t.date.startsWith(mo)));
  const monthTotals = monthRows.map(rows => sum(rows, t => t.amount));
  const monthCounts = monthRows.map(rows => rows.length);

  return (
    <div>
      <S.FiltersBar>
        <div className="field grow">
          <label>Apiary</label>
          <select
            value={selectedApiaryId}
            onChange={e => setSelectedApiaryId(e.target.value)}
          >
            {apiaries.map(a => (
              <option key={a.id} value={a.id}>
                {a.name} — {project(a.projectId)?.name || 'Project'}
              </option>
            ))}
          </select>
        </div>
      </S.FiltersBar>

      <S.KpiRow cols={3}>
        <S.KpiCard accent>
          <div className="lbl">Total Cost (Approved)</div>
          <div className="val">{fmt(totalCost)}</div>
          <div className="sub">{approvedCount} transactions</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">Pending Review</div>
          <div className="val">{fmt(pendingCost)}</div>
          <div className="sub">not yet in cost total</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">Avg. Cost / Transaction</div>
          <div className="val">{fmt(avgCost)}</div>
          <div className="sub">approved only</div>
        </S.KpiCard>
      </S.KpiRow>

      <S.Grid2>
        <S.CardBox>
          <h3>Cost by category</h3>
          <DonutChartSVG data={catTotals} labels={CATS} colors={CAT_COLOR} counts={catCounts} />
        </S.CardBox>
        <S.CardBox>
          <h3>Cost trend</h3>
          <VerticalBarChartSVG labels={monthLabels} data={monthTotals} counts={monthCounts} yearLabels={monthYearLabels} />
        </S.CardBox>
      </S.Grid2>

      <div style={{ fontSize: '13px', fontWeight: 800, margin: '22px 0 10px', color: '#4B4F5C', textTransform: 'uppercase' }}>
        Transactions for this apiary
      </div>

      <S.TableWrap>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Member</th>
              <th>Amount</th>
              <th>Category</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {apiaryRows.length ? (
              [...apiaryRows]
                .sort((a, b) => b.date.localeCompare(a.date))
                .map(t => {
                  const m = member(t.memberId);
                  return (
                    <tr key={t.id}>
                      <td>{fmtDate(t.date)}</td>
                      <td>
                        <span
                          className="member-link"
                          onClick={() => onOpenPassbook(m?.id || t.memberId)}
                        >
                          {m?.name || t.memberName || 'Staff'}
                        </span>
                      </td>
                      <td>
                        <strong>{fmt(t.amount)}</strong>
                      </td>
                      <td>
                        <S.Chip>{t.category}</S.Chip>
                      </td>
                      <td>
                        <S.Pill variant={t.approvalStatus}>
                          {t.approvalStatus.charAt(0).toUpperCase() + t.approvalStatus.slice(1)}
                        </S.Pill>
                      </td>
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                  No transactions logged for this apiary yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </S.TableWrap>
    </div>
  );
};

export default ApiaryPnLTab;
